import React from 'react';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import EmailBody from './EmailBody';
import { relativeTime } from '../domainMetrics';
import {
  senderName, senderAddress, spamState, replyMailto, domainLabel, isFeedback, tracksHandled,
} from '../emailHelpers';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const Row = ({ label, children }) => (
  <>
    <dt className="muted" style={{ fontSize: '13px' }}>{label}</dt>
    <dd style={{ margin: 0, fontSize: '13px', overflowWrap: 'anywhere' }}>{children}</dd>
  </>
);

/**
 * The reading pane. `onBack` renders a Back button (mobile / full-screen view).
 *
 * <EmailReader email onBack onDelete onToggleHandled />
 */
export default function EmailReader({ email, onBack, onDelete, onToggleHandled }) {
  if (!email) {
    return (
      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
        <EmptyState
          title="No message selected"
          message="Pick a message on the left, or use ↑ / ↓ and Enter."
        />
      </div>
    );
  }

  const feedback = isFeedback(email);
  const tracked = tracksHandled(email);
  // A reader's note carries no address to reply to.
  const mailto = feedback ? '' : replyMailto(email);
  const pageUrl = feedback && email.page && email.project_id
    ? `https://${domainLabel(email.project_id)}${email.page}` : '';

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div className="toolbar" style={{ marginBottom: 0 }}>
        {onBack && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        )}
        {tracked && onToggleHandled && (
          <button type="button" className={`btn btn-sm ${email.handled ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={() => onToggleHandled(email)}>
            {email.handled ? 'Mark unhandled' : 'Mark handled'}
          </button>
        )}
        {feedback ? (
          pageUrl && (
            <a className="btn btn-secondary btn-sm" href={pageUrl} target="_blank" rel="noopener noreferrer">
              View page ↗
            </a>
          )
        ) : mailto ? (
          <a className={`btn btn-sm ${tracked ? 'btn-secondary' : 'btn-primary'}`} href={mailto}>Reply</a>
        ) : (
          <button type="button" className="btn btn-primary btn-sm" disabled>Reply</button>
        )}
        {email.message_url && (
          <a className="btn btn-secondary btn-sm" href={email.message_url} target="_blank" rel="noopener noreferrer">
            Open original ↗
          </a>
        )}
        <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(String(email.id))}>
          Delete
        </button>
      </div>

      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, overflowWrap: 'anywhere' }}>
        {email.subject || '(no subject)'}
      </h2>

      <dl style={{
        display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px',
        margin: 0, paddingBottom: '12px', borderBottom: '1px solid var(--color-border)',
      }}>
        {feedback ? (
          <Row label="Vote">
            <Badge state={email.vote === 'up' ? 'success' : 'warn'}>
              {email.vote === 'up' ? '👍 Helpful' : '👎 Not helpful'}
            </Badge>
          </Row>
        ) : (
          <Row label="From">
            <span style={{ fontWeight: 600 }}>{senderName(email.sender)}</span>{' '}
            <span className="muted">&lt;{senderAddress(email.sender)}&gt;</span>
          </Row>
        )}
        {tracked && (
          <Row label="Status">
            <Badge state={email.handled ? 'info' : 'danger'}>{email.handled ? 'Handled' : 'Unhandled'}</Badge>
          </Row>
        )}
        {email.to && <Row label="To">{email.to}</Row>}
        <Row label="Date">
          {formatDate(email.created_at)}
          <span className="muted"> · {relativeTime(email.created_at)}</span>
        </Row>
        {email.project_id && (
          <Row label="Domain">
            <Badge state="info" title={email.project_id}>
              <span style={{ textTransform: 'none' }}>{domainLabel(email.project_id)}</span>
            </Badge>
          </Row>
        )}
        {email.spam_score != null && email.spam_score !== '' && (
          <Row label="Spam score">
            <Badge state={spamState(email.spam_score)}>{String(email.spam_score)}</Badge>
          </Row>
        )}
      </dl>

      <div style={{ maxHeight: '55vh', overflow: 'auto' }}>
        <EmailBody email={email} />
      </div>
    </div>
  );
}
